/**
 * useFactoryTimezone Hook
 *
 * Custom hook for accessing the active factory's timezone settings.
 * Reads from the Jotai factory atoms populated by AuthProvider / FactorySelector.
 */

import { useAtomValue } from 'jotai';
import { Factory } from '@/schemas/factory';
import {
  currentFactoryDerivedAtom,
  factoryErrorAtom,
  factoryLoadingAtom,
  factoryTimeZoneAtom,
} from '@/stores/atoms/factory.atoms';

/**
 * Factory timezone configuration interface
 */
interface FactoryTimezoneConfig {
  timezone: string;
  isLoading: boolean;
  error: string | null;
  factory?: Factory;
}

/**
 * Custom hook to access factory timezone settings from the active factory selection.
 */
export function useFactoryTimezone(): FactoryTimezoneConfig {
  return {
    timezone: useAtomValue(factoryTimeZoneAtom),
    factory: useAtomValue(currentFactoryDerivedAtom),
    isLoading: useAtomValue(factoryLoadingAtom),
    error: useAtomValue(factoryErrorAtom),
  };
}

/**
 * Hook for getting factory timezone without loading state
 */
export function useFactoryTimezoneValue(): string {
  const { timezone, isLoading } = useFactoryTimezone();

  // Return UTC as fallback while loading
  return isLoading ? 'UTC' : timezone;
}

/**
 * Hook for getting the full factory object
 */
export function useFactory(): {
  factory: Factory | undefined;
  isLoading: boolean;
  error: string | null;
} {
  const { factory, isLoading, error } = useFactoryTimezone();

  return {
    factory,
    isLoading,
    error,
  };
}
